import { useState, useCallback, useEffect, useRef, forwardRef } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  createParty,
  filterParties,
  getPartyById,
  type PartyResponse,
} from '@/api/party.api';
import { MODULES } from '@/utils/permissions';
import { useWarehouseStore } from '@/stores/warehouseStore';

// ── Indian states list with GST code ─────────────────────────────────────────
interface StateOption { name: string; code: string }
const INDIAN_STATES: StateOption[] = [
  { name: 'Jammu & Kashmir', code: '01' },
  { name: 'Himachal Pradesh', code: '02' },
  { name: 'Punjab', code: '03' },
  { name: 'Chandigarh', code: '04' },
  { name: 'Uttarakhand', code: '05' },
  { name: 'Haryana', code: '06' },
  { name: 'Delhi', code: '07' },
  { name: 'Rajasthan', code: '08' },
  { name: 'Uttar Pradesh', code: '09' },
  { name: 'Bihar', code: '10' },
  { name: 'Sikkim', code: '11' },
  { name: 'Arunachal Pradesh', code: '12' },
  { name: 'Nagaland', code: '13' },
  { name: 'Manipur', code: '14' },
  { name: 'Mizoram', code: '15' },
  { name: 'Tripura', code: '16' },
  { name: 'Meghalaya', code: '17' },
  { name: 'Assam', code: '18' },
  { name: 'West Bengal', code: '19' },
  { name: 'Jharkhand', code: '20' },
  { name: 'Odisha', code: '21' },
  { name: 'Chhattisgarh', code: '22' },
  { name: 'Madhya Pradesh', code: '23' },
  { name: 'Gujarat', code: '24' },
  { name: 'Daman & Diu', code: '25' },
  { name: 'Dadra & Nagar Haveli', code: '26' },
  { name: 'Maharashtra', code: '27' },
  { name: 'Andhra Pradesh (old)', code: '28' },
  { name: 'Karnataka', code: '29' },
  { name: 'Goa', code: '30' },
  { name: 'Lakshadweep', code: '31' },
  { name: 'Kerala', code: '32' },
  { name: 'Tamil Nadu', code: '33' },
  { name: 'Puducherry', code: '34' },
  { name: 'Andaman & Nicobar', code: '35' },
  { name: 'Telangana', code: '36' },
  { name: 'Andhra Pradesh', code: '37' },
  { name: 'Ladakh', code: '38' },
];

interface StateDropdownProps {
  value: StateOption | null;
  onChange: (s: StateOption | null) => void;
  className?: string;
  onStateSelected?: () => void;
}

const StateDropdown = forwardRef<HTMLButtonElement, StateDropdownProps>(function StateDropdown(
  { value, onChange, className, onStateSelected },
  ref
) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = INDIAN_STATES.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.code.includes(search)
  );

  useEffect(() => { setHighlightIdx(-1); }, [search]);

  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-state-option]');
      items[highlightIdx]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
        setHighlightIdx(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectState = (s: StateOption) => {
    onChange(s);
    setOpen(false);
    setSearch('');
    setHighlightIdx(-1);
    setTimeout(() => onStateSelected?.(), 50);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx >= 0 && filtered[highlightIdx]) {
        selectState(filtered[highlightIdx]);
      } else if (filtered.length === 1) {
        selectState(filtered[0]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
      setHighlightIdx(-1);
    }
  };

  return (
    <div className="relative w-full" ref={wrapRef}>
      <button
        ref={ref}
        type="button"
        onClick={() => { setOpen((v) => !v); setSearch(''); setHighlightIdx(-1); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
            setSearch('');
            setHighlightIdx(-1);
          }
        }}
        className={`${className} flex items-center justify-between gap-1 text-left`}
      >
        <span className={value ? 'text-slate-800' : 'text-slate-400'}>
          {value ? `${value.name} (${value.code})` : 'Select state...'}
        </span>
        <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-slate-400 text-xs shrink-0`} />
      </button>

      {open && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-[#e2e8f0] rounded-lg shadow-lg">
          <div className="p-1.5 border-b border-[#f1f5f9]">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search state... (↑↓ to navigate, Enter to select)"
              autoFocus
              className="w-full h-7 px-2 text-xs border border-[#e2e8f0] rounded focus:outline-none focus:border-[#4f46e5]"
            />
          </div>
          <div className="max-h-40 overflow-y-auto" ref={listRef}>
            {filtered.map((s, idx) => (
              <button
                key={s.code}
                data-state-option
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectState(s);
                }}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between transition-colors ${
                  idx === highlightIdx
                    ? 'bg-indigo-100 text-[#4338ca] font-semibold'
                    : value?.code === s.code
                    ? 'bg-indigo-50 text-[#4f46e5] font-semibold'
                    : 'hover:bg-indigo-50 text-slate-700'
                }`}
              >
                <span>{s.name}</span>
                <span className="text-slate-400 font-mono">{s.code}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-xs text-slate-400">No states found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export interface CustomerInfo {
  id: string;
  name: string;
  gstin: string;
  phone: string;
  stateCode: string;
  billingAddress: string;
  shippingAddress: string;
  creditLimit?: number;
  openingBalance?: number;
  balance?: number;
  warehouse_id?: string;
  warehouse_name?: string;
  gstinStatus?: string;
  gstinVerified?: boolean;
  pan?: string;
  isTdsApplicable?: boolean;
  tdsSection?: string;
  tdsRate?: number;
}

interface Props {
  selected: CustomerInfo | null;
  onSelect: (c: CustomerInfo) => void;
  onClear?: () => void;
  billingAddress: string;
  shippingAddress: string;
  onBillingAddressChange: (v: string) => void;
  onShippingAddressChange: (v: string) => void;
}

interface NewCustomerForm {
  name: string;
  mobile: string;
  email: string;
  gstin: string;
  state: StateOption | null;
  city: string;
}

const emptyForm = (): NewCustomerForm => ({
  name: '',
  mobile: '',
  email: '',
  gstin: '',
  state: null,
  city: '',
});

const mapPartyToCustomer = (party: PartyResponse): CustomerInfo => {
  const gstinStr = (party.gstin ?? (party as any).gst_in ?? '').trim();
  const phoneVal = String(
    party.phone ??
    party.mobile ??
    (party as any).phone_number ??
    (party as any).contact_number ??
    ''
  ).trim();

  const stateCodeVal = String(
    party.stateCode ??
    party.state_code ??
    (party as any).state_number ??
    (gstinStr.length >= 2 ? gstinStr.substring(0, 2) : '')
  ).trim();

  const billingAddr = String(
    party.billingAddress ??
    party.billing_address ??
    party.city ??
    (party as any).address ??
    ''
  ).trim();

  const shippingAddr = String(
    party.shippingAddress ??
    party.shipping_address ??
    party.billingAddress ??
    party.billing_address ??
    party.city ??
    (party as any).address ??
    ''
  ).trim();

  const creditLimitVal = Number(
    party.creditLimit ?? party.credit_limit ?? 0
  ) || 0;

  const openingBalanceVal = Number(
    party.openingBalance ?? party.opening_balance ?? 0
  ) || 0;

  const balanceVal = Number(
    party.balance ??
    (party as unknown as { balance?: number }).balance ??
    party.openingBalance ??
    party.opening_balance ??
    0
  ) || 0;

  const warehouseIdVal = String(
    party.warehouse_id ??
    (party as any).warehouseId ??
    ''
  ).trim();

  const warehouseNameVal = String(
    party.warehouse_name ??
    (party as any).warehouseName ??
    (party as any).warehouse?.name ??
    ''
  ).trim();

  return {
    id: party.id,
    name: party.name,
    gstin: gstinStr,
    phone: phoneVal,
    stateCode: stateCodeVal,
    billingAddress: billingAddr,
    shippingAddress: shippingAddr,
    creditLimit: creditLimitVal,
    openingBalance: openingBalanceVal,
    balance: balanceVal,
    warehouse_id: warehouseIdVal,
    warehouse_name: warehouseNameVal,
    gstinStatus: party.gstin_status ?? (party as any).gstinStatus ?? undefined,
    gstinVerified: party.gstin_verified ?? (party as any).gstinVerified ?? undefined,
    pan: party.pan ?? undefined,
    isTdsApplicable: party.is_tds_applicable ?? undefined,
    tdsSection: party.tds_section ?? undefined,
    tdsRate: party.tds_rate !== undefined && party.tds_rate !== null ? Number(party.tds_rate) : undefined,
  };
};

export default function CustomerSection({
  selected,
  onSelect,
  onClear,
  billingAddress,
  shippingAddress,
  onBillingAddressChange,
  onShippingAddressChange,
}: Props) {
  const [query, setQuery] = useState(selected?.name ?? '');
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState<NewCustomerForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [mobileError, setMobileError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [saveError, setSaveError] = useState('');
  const { hasPermission } = useAuth();
  const canAddParty = hasPermission(MODULES.PARTIES, 'create');

  // Dropdown state
  const [options, setOptions] = useState<CustomerInfo[]>([]);
  const [allCustomers, setAllCustomers] = useState<CustomerInfo[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const { selectedWarehouseId, selectedWarehouseName } = useWarehouseStore();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const gstinInputRef = useRef<HTMLInputElement>(null);
  const stateDropdownRef = useRef<HTMLButtonElement>(null);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const saveNewBtnRef = useRef<HTMLButtonElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestSelectedIdRef = useRef<string | null>(null);

  const toast = useToast();

  // Fetch all customers for the respective warehouse
  const fetchAllCustomers = useCallback(
    async (openDropdown: boolean = false) => {
      setLoadingOptions(true);
      try {
        const warehouseParam =
          selectedWarehouseId && selectedWarehouseId !== 'ALL'
            ? selectedWarehouseId
            : undefined;

        // Fetch parties for the respective warehouse
        let response = await filterParties({
          isActive: true,
          warehouse_id: warehouseParam,
        });

        let rawData = response.data ?? [];

        // If warehouse-specific fetch returned 0 parties, fallback to all active company parties
        if (rawData.length === 0 && warehouseParam) {
          response = await filterParties({ isActive: true });
          rawData = response.data ?? [];
        }

        const customerParties = rawData.filter((party: PartyResponse) => {
          const type = String(party.type || '').toLowerCase();
          return type === 'customer' || type === 'both';
        });

        const mapped = customerParties.map(mapPartyToCustomer);
        mapped.sort((a, b) => a.name.localeCompare(b.name));

        setAllCustomers(mapped);
        setOptions(mapped);
        if (openDropdown) {
          setShowDropdown(true);
        }
        setHighlightedIdx(-1);
      } catch (err) {
        console.error('Fetch all customers error:', err);
        setAllCustomers([]);
        setOptions([]);
      } finally {
        setLoadingOptions(false);
      }
    },
    [selectedWarehouseId],
  );

  // Pre-fetch on mount & whenever warehouse selection changes
  useEffect(() => {
    setAllCustomers([]);
    setOptions([]);
    setShowDropdown(false);
    fetchAllCustomers(false);
  }, [selectedWarehouseId, fetchAllCustomers]);

  // Sync query when selected customer changes
  useEffect(() => {
    setQuery(selected?.name ?? '');
  }, [selected?.id, selected?.name]);

  // Auto-focus Full Name when the new customer form opens
  useEffect(() => {
    if (showNewForm) {
      const t = setTimeout(() => nameInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [showNewForm]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
        setHighlightedIdx(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Filter customers based on search query
  const filterCustomers = useCallback(
    (q: string) => {
      if (!q.trim()) {
        setOptions(allCustomers);
        return;
      }
      const searchLower = q.toLowerCase();
      const filtered = allCustomers.filter(
        (c) =>
          c.name.toLowerCase().includes(searchLower) ||
          c.phone.toLowerCase().includes(searchLower) ||
          c.gstin.toLowerCase().includes(searchLower),
      );
      setOptions(filtered);
    },
    [allCustomers],
  );

  const handleInputChange = (val: string) => {
    setQuery(val);

    if (!val.trim() && selected) {
      onClear?.();
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (allCustomers.length === 0) {
        fetchAllCustomers(true);
      } else {
        filterCustomers(val);
        setShowDropdown(true);
      }
    }, 300);
  };

  const handleClearSelection = () => {
    setQuery('');
    setOptions(allCustomers);
    setShowDropdown(true);
    onClear?.();
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSelect = useCallback(
    (c: CustomerInfo) => {
      setQuery(c.name);
      setShowDropdown(false);
      setHighlightedIdx(-1);
      onSelect(c);
      setShowNewForm(false);

      latestSelectedIdRef.current = c.id;
      void (async () => {
        try {
          const fresh = await getPartyById(c.id);
          if (latestSelectedIdRef.current !== c.id) return;
          if (fresh.data) {
            onSelect(mapPartyToCustomer(fresh.data));
          }
        } catch {
          // Keep cached values selected above
        }
      })();
    },
    [onSelect],
  );

  const handleCreateNewCustomerClick = useCallback((name: string) => {
    setNewForm({
      name: name,
      mobile: '',
      email: '',
      gstin: '',
      state: null,
      city: '',
    });
    setMobileError('');
    setEmailError('');
    setSaveError('');
    setShowNewForm(true);
    setShowDropdown(false);
  }, []);

  // Arrow key navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || (options.length === 0 && !query.trim())) {
      if (e.key === 'ArrowDown' && allCustomers.length === 0) {
        fetchAllCustomers(true);
      }
      return;
    }
    const hasCreateButton = query.trim() && !options.some((o) => o.name.toLowerCase() === query.trim().toLowerCase());
    const totalItems = options.length + (hasCreateButton ? 1 : 0);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx((prev) => Math.min(prev + 1, totalItems - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIdx >= 0) {
        if (highlightedIdx < options.length) {
          handleSelect(options[highlightedIdx]);
        } else if (highlightedIdx === options.length && hasCreateButton) {
          handleCreateNewCustomerClick(query.trim());
        }
      } else {
        const exact = options.find((o) => o.name.toLowerCase() === query.trim().toLowerCase());
        if (exact) {
          handleSelect(exact);
        } else if (hasCreateButton) {
          handleCreateNewCustomerClick(query.trim());
        }
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setHighlightedIdx(-1);
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIdx >= 0 && dropdownRef.current) {
      const items = dropdownRef.current.querySelectorAll('[data-option]');
      items[highlightedIdx]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIdx]);

  const handleSaveNew = useCallback(async () => {
    setSaveError('');
    setMobileError('');
    setEmailError('');

    if (!newForm.name.trim()) return;

    const mobileVal = newForm.mobile.trim();
    if (!mobileVal || mobileVal.length !== 10) {
      setMobileError('Mobile must be exactly 10 digits');
      return;
    }

    const emailVal = newForm.email.trim();
    if (!emailVal) {
      setEmailError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      setEmailError('Enter a valid email address');
      return;
    }

    setSaving(true);
    try {
      const warehouseParam = selectedWarehouseId && selectedWarehouseId !== 'ALL'
        ? selectedWarehouseId : undefined;
      const dupRes = await filterParties({ isActive: true, warehouse_id: warehouseParam });
      const allData = dupRes.data ?? [];

      const phoneExists = allData.some(
        (p) => p.phone === mobileVal || p.mobile === mobileVal,
      );
      const emailExists = allData.some(
        (p) => typeof p.email === 'string' && p.email.toLowerCase() === emailVal.toLowerCase(),
      );

      if (phoneExists || emailExists) {
        if (phoneExists) setMobileError('This mobile number is already registered');
        if (emailExists) setEmailError('This email is already registered');
        return;
      }

      const created = await createParty({
        name: newForm.name.trim(),
        type: 'Customer',
        mobile: mobileVal,
        phone: mobileVal,
        email: emailVal,
        gstin: newForm.gstin.trim() || undefined,
        city: newForm.city.trim() || undefined,
        billingAddress: newForm.city.trim() || undefined,
        shippingAddress: newForm.city.trim() || undefined,
        stateCode: newForm.state?.code || undefined,
        stateName: newForm.state?.name || undefined,
        warehouseId: selectedWarehouseId && selectedWarehouseId !== 'ALL'
          ? selectedWarehouseId : undefined,
        warehouseName: selectedWarehouseId && selectedWarehouseName !== 'ALL'
          ? selectedWarehouseName : undefined,
        isActive: true,
      });

      if (!created.success || !created.data) {
        throw new Error(created.message || 'Failed to create customer');
      }

      const newCustomer = mapPartyToCustomer(created.data);
      setAllCustomers((prev) => [...prev.filter((c) => c.id !== newCustomer.id), newCustomer]);
      setOptions((prev) => [...prev.filter((c) => c.id !== newCustomer.id), newCustomer]);
      setShowNewForm(false);
      setNewForm(emptyForm());
      setMobileError('');
      setEmailError('');
      setSaveError('');
      handleSelect(newCustomer);
      toast.success('Customer added and selected');
    } catch (err: any) {
      console.error(err);
      const msg: string = err?.message || '';
      const lo = msg.toLowerCase();
      if (lo.includes('phone') || lo.includes('mobile')) {
        setMobileError('This mobile number is already registered');
      } else if (lo.includes('email')) {
        setEmailError('This email is already registered');
      } else {
        setSaveError(msg || 'Failed to save customer. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }, [newForm, handleSelect, selectedWarehouseId, selectedWarehouseName, toast]);

  const fl =
    'w-full h-8 px-2 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-indigo-200';
  const lb = 'block text-xs text-slate-500 mb-0.5';

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 h-full">
      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
        Customer
      </h3>

      {/* Search + New */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 relative" ref={dropdownRef}>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (showNewForm) return;
              if (allCustomers.length === 0) {
                fetchAllCustomers(true);
              } else {
                if (!query.trim()) {
                  setOptions(allCustomers);
                } else {
                  filterCustomers(query);
                }
                setShowDropdown(true);
              }
            }}
            placeholder="Search customer..."
            className={`w-full h-9 ${query || selected ? 'pl-3 pr-8' : 'px-3'} text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-indigo-200`}
          />
          {loadingOptions ? (
            <i className="ri-loader-4-line animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
          ) : (query || selected) ? (
            <button
              type="button"
              onClick={handleClearSelection}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-base flex items-center justify-center p-0.5 rounded cursor-pointer"
              title="Clear customer selection"
            >
              <i className="ri-close-line" />
            </button>
          ) : null}

          {/* Dropdown */}
          {showDropdown && (options.length > 0 || query.trim() || loadingOptions) && (
            <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-[#e2e8f0] rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {loadingOptions && allCustomers.length === 0 && (
                <div className="px-3 py-3 text-xs text-slate-500 flex items-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  Loading customers...
                </div>
              )}
              {!loadingOptions && options.map((c, idx) => (
                <button
                  key={c.id}
                  data-option
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(c);
                  }}
                  onMouseEnter={() => setHighlightedIdx(idx)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 text-left text-sm transition-colors ${
                    idx === highlightedIdx
                      ? 'bg-indigo-50 text-[#4f46e5]'
                      : 'hover:bg-slate-50 text-[#1e293b]'
                  }`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{c.name}</span>
                    {c.phone && <span className="text-xs text-slate-500">{c.phone}</span>}
                  </div>

                  {c.gstin && (
                    <span className="text-xs text-slate-400 ml-2 shrink-0">
                      {c.gstin}
                    </span>
                  )}
                </button>
              ))}

              {!loadingOptions && options.length === 0 && query.trim() && (
                <div className="px-3 py-3 text-xs text-slate-400 border-b border-[#f1f5f9] last:border-b-0">
                  No customers found for "{query}"
                </div>
              )}

              {query.trim() && !options.some((o) => o.name.toLowerCase() === query.trim().toLowerCase()) && (
                <button
                  data-option
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleCreateNewCustomerClick(query.trim());
                  }}
                  onMouseEnter={() => setHighlightedIdx(options.length)}
                  className={`w-full text-left flex items-center gap-2 px-3 py-3 text-sm text-[#4f46e5] font-semibold transition-colors border-t border-[#e2e8f0] bg-indigo-50/30 ${
                    highlightedIdx === options.length ? 'bg-indigo-100 text-[#4338ca]' : 'hover:bg-indigo-50'
                  }`}
                >
                  <i className="ri-user-add-line text-base" />
                  Create Customer "{query.trim()}"
                </button>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setShowNewForm((v) => !v)}
          className="shrink-0 flex items-center gap-1 h-9 px-3 text-xs font-semibold text-[#4f46e5] border border-indigo-200 rounded-lg hover:bg-indigo-50 cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-user-add-line" />
          {showNewForm ? 'Cancel' : '+ New'}
        </button>
      </div>

      {/* New customer form */}
      {showNewForm && canAddParty && (
        <div className="mb-3 p-3 bg-slate-50 border border-[#e2e8f0] rounded-lg">
          <p className="text-xs font-semibold text-slate-600 mb-2">
            New Customer
          </p>
          <div className="grid grid-cols-2 gap-2 mb-2">
            {/* Name */}
            <div>
              <label className={lb}>
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                ref={nameInputRef}
                type="text"
                value={newForm.name}
                onChange={(e) =>
                  setNewForm((f) => ({ ...f, name: e.target.value }))
                }
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); mobileInputRef.current?.focus(); } }}
                className={fl}
                placeholder="Customer name"
              />
            </div>

            {/* Mobile */}
            <div>
              <label className={lb}>
                Mobile <span className="text-red-500">*</span>
              </label>
              <input
                ref={mobileInputRef}
                type="tel"
                value={newForm.mobile}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setNewForm((f) => ({ ...f, mobile: val }));
                  setMobileError('');
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); emailInputRef.current?.focus(); } }}
                className={`${fl} ${mobileError ? 'border-red-400' : ''}`}
                placeholder="10-digit mobile"
                maxLength={10}
              />
              {mobileError && (
                <p className="text-xs text-red-500 mt-0.5">{mobileError}</p>
              )}
              {newForm.mobile && newForm.mobile.length < 10 && !mobileError && (
                <p className="text-xs text-amber-500 mt-0.5">
                  {newForm.mobile.length}/10 digits
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className={lb}>
                Email <span className="text-red-500">*</span>
              </label>
              <input
                ref={emailInputRef}
                type="email"
                value={newForm.email}
                onChange={(e) => {
                  setNewForm((f) => ({ ...f, email: e.target.value }));
                  setEmailError('');
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); gstinInputRef.current?.focus(); } }}
                placeholder="customer@example.com"
                className={`${fl} ${emailError ? 'border-red-400' : ''}`}
              />
              {emailError && (
                <p className="text-xs text-red-500 mt-0.5">{emailError}</p>
              )}
            </div>

            {/* GSTIN */}
            <div>
              <label className={lb}>GSTIN (optional)</label>
              <input
                ref={gstinInputRef}
                type="text"
                value={newForm.gstin}
                onChange={(e) =>
                  setNewForm((f) => ({
                    ...f,
                    gstin: e.target.value.toUpperCase(),
                  }))
                }
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); stateDropdownRef.current?.focus(); stateDropdownRef.current?.click(); } }}
                className={fl}
                placeholder="15-char GSTIN"
                maxLength={15}
              />
            </div>

            {/* State */}
            <div className="col-span-2">
              <label className={lb}>State</label>
              <StateDropdown
                ref={stateDropdownRef}
                value={newForm.state}
                onChange={(s) => setNewForm((f) => ({ ...f, state: s }))}
                onStateSelected={() => cityInputRef.current?.focus()}
                className={fl}
              />
            </div>

            {/* City */}
            <div className="col-span-2">
              <label className={lb}>City</label>
              <input
                ref={cityInputRef}
                type="text"
                value={newForm.city}
                onChange={(e) =>
                  setNewForm((f) => ({ ...f, city: e.target.value }))
                }
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveNewBtnRef.current?.click(); } }}
                className={fl}
                placeholder="City"
              />
            </div>
          </div>

          {saveError && (
            <p className="text-xs text-red-500 mb-2 flex items-center gap-1">
              <i className="ri-error-warning-line" /> {saveError}
            </p>
          )}

          <div className="flex gap-2">
            <button
              ref={saveNewBtnRef}
              type="button"
              onClick={handleSaveNew}
              disabled={
                saving || !newForm.name.trim() || !newForm.mobile.trim() || !newForm.email.trim()
              }
              className="flex items-center gap-1 h-7 px-3 text-xs font-semibold bg-[#4f46e5] text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 cursor-pointer whitespace-nowrap"
            >
              {saving ? (
                <i className="ri-loader-4-line animate-spin" />
              ) : (
                <i className="ri-save-line" />
              )}
              Save &amp; Select
            </button>
            <button
              type="button"
              onClick={() => {
                setShowNewForm(false);
                setNewForm(emptyForm());
                setMobileError('');
                setEmailError('');
                setSaveError('');
              }}
              className="flex items-center gap-1 h-7 px-3 text-xs font-medium text-slate-500 border border-[#e2e8f0] rounded-lg hover:bg-slate-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Selected customer details */}
      {selected && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={lb}>Mobile</label>
              <div className="h-8 px-2 flex items-center text-sm text-slate-700 bg-slate-50 border border-[#e2e8f0] rounded-lg">
                {selected.phone || '—'}
              </div>
            </div>
            <div>
              <label className={lb}>GSTIN</label>
              <div className="h-8 px-2 flex items-center text-xs text-slate-700 bg-slate-50 border border-[#e2e8f0] rounded-lg truncate">
                {selected.gstin || '—'}
              </div>
            </div>
          </div>
          <div>
            <label className={lb}>Billing Address</label>
            <textarea
              value={billingAddress}
              onChange={(e) => onBillingAddressChange(e.target.value)}
              rows={2}
              className="w-full px-2 py-1.5 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-indigo-200 resize-none"
              placeholder="Billing address"
            />
          </div>
          <div>
            <label className={lb}>Shipping Address</label>
            <textarea
              value={shippingAddress}
              onChange={(e) => onShippingAddressChange(e.target.value)}
              rows={2}
              className="w-full px-2 py-1.5 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-indigo-200 resize-none"
              placeholder="Same as billing"
            />
          </div>
        </div>
      )}
    </div>
  );
}
